/**
 * Tabbed content. Builds the tab strip from each panel's `data-tab-title`, then shows
 * one panel at a time. Kept out of the panels themselves so the merchant only writes a
 * title and some copy per tab and the strip follows.
 *
 * The strip scrolls sideways when it doesn't fit, and it walks itself along: choose
 * the tab at the edge of what's visible and the one after it is scrolled into view,
 * so the next click is already reachable. No arrows — they would be a second
 * control for something choosing a tab already does.
 */
(() => {
  class ProductTabs extends HTMLElement {
    connectedCallback() {
      this.panels = [...this.querySelectorAll('[data-tab-panel]')];
      this.nav = this.querySelector('[data-tabs-nav]');
      if (!this.panels.length || !this.nav) return;

      // Rebuild the strip (connectedCallback can run again after an editor re-render).
      this.nav.replaceChildren();

      this.tabs = this.panels.map((panel, index) => {
        const tab = document.createElement('button');
        tab.type = 'button';
        tab.className = 'tabs__tab';
        tab.setAttribute('role', 'tab');
        tab.textContent = panel.dataset.tabTitle || `Tab ${index + 1}`;
        tab.addEventListener('click', () => this.#activate(index));
        this.nav.appendChild(tab);
        return tab;
      });

      // The first tab is selected, not chosen: nothing should move under the
      // reader before they have touched anything.
      this.#activate(0, false);
      this.classList.add('tabs--ready');

      // Only drives the edge fade — there is nothing to click, so it is kept out
      // of #reveal and simply follows whatever the strip is doing.
      this.nav.addEventListener('scroll', () => this.#syncEdges(), { passive: true });
      this.#syncEdges();
      this.resize = new ResizeObserver(() => this.#syncEdges());
      this.resize.observe(this.nav);
    }

    disconnectedCallback() {
      this.resize?.disconnect();
    }

    #syncEdges() {
      const { scrollLeft, scrollWidth, clientWidth } = this.nav;
      // A pixel of slack: sub-pixel layout leaves a fraction at either end, and a
      // fade over nothing looks like a rendering fault of its own.
      this.nav.classList.toggle('can-prev', scrollLeft > 1);
      this.nav.classList.toggle('can-next', scrollLeft + clientWidth < scrollWidth - 1);
    }

    #activate(index, scroll = true) {
      this.panels.forEach((panel, i) => {
        panel.hidden = i !== index;
      });
      this.tabs.forEach((tab, i) => {
        tab.setAttribute('aria-selected', i === index ? 'true' : 'false');
      });

      if (scroll) this.#reveal(index);
      this.active = index;
    }

    /*
      Scrolls so that the chosen tab is fully visible AND so is its neighbour in the
      direction of travel. Revealing the neighbour is the whole trick: it means the
      tab at the edge of the strip is never the last one you can reach by clicking.

      Positions come from `offsetLeft`, which is a layout position and so unaffected
      by how far the strip is currently scrolled.
    */
    #reveal(index) {
      const nav = this.nav;
      if (nav.scrollWidth <= nav.clientWidth) return;

      const step = index >= (this.active ?? index) ? 1 : -1;
      const tab = this.tabs[index];
      const neighbour = this.tabs[index + step];
      const pad = 24;

      let target = nav.scrollLeft;
      const fitEnd = (el) => {
        const right = el.offsetLeft + el.offsetWidth;
        if (right > target + nav.clientWidth) target = right - nav.clientWidth + pad;
      };
      const fitStart = (el) => {
        if (el.offsetLeft < target) target = el.offsetLeft - pad;
      };

      // The neighbour first, then the tab itself — so if both cannot fit, the tab
      // the visitor actually chose is the one guaranteed to be on screen.
      if (neighbour) (step > 0 ? fitEnd : fitStart)(neighbour);
      fitEnd(tab);
      fitStart(tab);

      const max = nav.scrollWidth - nav.clientWidth;
      nav.scrollTo({ left: Math.max(0, Math.min(target, max)), behavior: 'smooth' });
      // `scroll` fires as it animates, but syncing now keeps the fade honest if the
      // browser jumps straight there (reduced motion, or no smooth support).
      this.#syncEdges();
    }
  }

  if (!customElements.get('product-tabs')) {
    customElements.define('product-tabs', ProductTabs);
  }
})();
